import { useState, useEffect, useCallback, useRef, useId, Children, isValidElement, cloneElement } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { parseInrInput, formatInrFriendly } from '../lib/inr';
import {
  FilmStrip,
  SignOut,
  Users,
  CurrencyDollar,
  TrendUp,
  ChatCircleDots,
  Shield,
  Trash,
  UserGear,
  Plus,
  X,
  Key,
  ClockCounterClockwise,
  CheckCircle,
  XCircle,
  SealCheck,
  Crown,
  Gavel,
} from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import Modal from '../components/Modal';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ROLES = ['fan', 'investor', 'producer', 'admin'];
const INDUSTRIES = ['Tollywood', 'Bollywood', 'Kollywood', 'Hollywood'];

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [movies, setMovies] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [producerProjects, setProducerProjects] = useState([]);
  const [pappaPending, setPappaPending] = useState([]);
  const [founderOverview, setFounderOverview] = useState(null);
  const [founderInvestors, setFounderInvestors] = useState([]);
  const [investorDetail, setInvestorDetail] = useState(null);
  const [movieCashflow, setMovieCashflow] = useState(null);
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyNotes, setVerifyNotes] = useState('');
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateMovie, setShowCreateMovie] = useState(false);
  const [editMovie, setEditMovie] = useState(null);
  const [resetTarget, setResetTarget] = useState(null); // {id,email}
  const [myTier, setMyTier] = useState(null); // 'founder' | 'admin' | 'account_admin'
  const [pendingMovies, setPendingMovies] = useState([]);
  const [rejectedMovies, setRejectedMovies] = useState([]);
  const [allAdmins, setAllAdmins] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null); // {id, title}
  const [deleteTarget, setDeleteTarget] = useState(null); // {type: 'user'|'movie', id}
  const [rejectReason, setRejectReason] = useState('');
  const [grantEmail, setGrantEmail] = useState('');
  const [grantTier, setGrantTier] = useState('admin');

  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }),
    []
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Determine my tier first — this drives what I am allowed to fetch.
      const meRes = await axios.get(`${BACKEND_URL}/api/admin/me/tier`, authHeaders());
      const tier = meRes.data?.admin_tier || 'admin';
      setMyTier(tier);

      // 2. Settled fetches so a 403 on one panel doesn't blank the whole dashboard.
      const safe = (p) => p.then((r) => r.data).catch(() => null);
      const [s, u, m, i, l, pp, pa, fo, fi, pend, rej, admins] = await Promise.all([
        safe(axios.get(`${BACKEND_URL}/api/admin/stats`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/users`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/movies`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/investments`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/login-logs?limit=200`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/producer-projects`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/pappa-pending`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/founder/overview`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/founder/investors`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/movies/pending`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/movies/by-status?status=rejected`, authHeaders())),
        safe(axios.get(`${BACKEND_URL}/api/admin/admins`, authHeaders())),
      ]);
      if (s) setStats(s);
      if (u) setUsers(u);
      if (m) setMovies(m);
      if (i) setInvestments(i);
      if (l) setLoginLogs(l);
      if (pp) setProducerProjects(pp);
      if (pa) setPappaPending(pa);
      if (fo) setFounderOverview(fo);
      if (fi) setFounderInvestors(fi);
      setPendingMovies(pend || []);
      setRejectedMovies(rej || []);
      setAllAdmins(admins || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const handleRoleChange = async (userId, role) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/users/${userId}/role`, { role }, authHeaders());
      toast.success('Role updated');
      setUsers((prev) => prev.map((x) => (x.id === userId ? { ...x, role } : x)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, authHeaders());
      toast.success('User deleted');
      setUsers((prev) => prev.filter((x) => x.id !== userId));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleDeleteMovie = async (movieId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/movies/${movieId}`, authHeaders());
      toast.success('Movie removed');
      setMovies((prev) => prev.filter((m) => m.id !== movieId));
    } catch (error) {
      toast.error('Failed to remove');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleResetPassword = async (newPassword) => {
    if (!resetTarget) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${resetTarget.id}/reset-password`,
        { new_password: newPassword },
        authHeaders()
      );
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleCreateUser = async (data) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/users`, data, authHeaders());
      toast.success(`User ${res.data.email} created`);
      setUsers((prev) => [...prev, res.data]);
      setShowCreateUser(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleCreateMovie = async (data) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/movies`, data, authHeaders());
      toast.success(`Movie "${res.data.title}" created`);
      setMovies((prev) => [...prev, res.data]);
      setShowCreateMovie(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create movie');
    }
  };

  const handleEditMovie = async (movieId, data) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/movies/${movieId}`, data, authHeaders());
      toast.success(`Movie "${res.data.title}" updated`);
      setMovies((prev) => prev.map((m) => (m.id === movieId ? res.data : m)));
      setEditMovie(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update movie');
    }
  };

  const handleApproveMovie = async (movieId) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/movies/${movieId}/approve`, {}, authHeaders());
      toast.success(`"${res.data.title}" approved and live`);
      setPendingMovies((prev) => prev.filter((m) => m.id !== movieId));
      setMovies((prev) => {
        const exists = prev.some((m) => m.id === movieId);
        return exists ? prev.map((m) => (m.id === movieId ? res.data : m)) : [...prev, res.data];
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectMovie = async () => {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (reason.length < 3) {
      toast.error('Please provide a rejection reason (min 3 characters)');
      return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/movies/${rejectTarget.id}/reject`,
        { reason },
        authHeaders()
      );
      toast.success(`"${res.data.title}" rejected`);
      setPendingMovies((prev) => prev.filter((m) => m.id !== rejectTarget.id));
      setRejectedMovies((prev) => [res.data, ...prev]);
      setRejectTarget(null);
      setRejectReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleGrantAdmin = async () => {
    const email = grantEmail.trim();
    if (!email) {
      toast.error('Enter the user email');
      return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/grant-admin`,
        { email, admin_tier: grantTier },
        authHeaders()
      );
      toast.success(`Granted ${grantTier} to ${email}`);
      setGrantEmail('');
      // Refresh admin list
      const fresh = await axios.get(`${BACKEND_URL}/api/admin/admins`, authHeaders());
      setAllAdmins(fresh.data);
      void res;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant admin');
    }
  };

  const handleChangeTier = async (userId, newTier) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${userId}/tier`,
        { admin_tier: newTier },
        authHeaders()
      );
      toast.success('Tier updated');
      setAllAdmins((prev) => prev.map((a) => (a.id === userId ? { ...a, admin_tier: newTier } : a)));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tier');
    }
  };

  const fmtUsd = (n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold" />
      </div>
    );
  }
  const safeStats = stats || { total_users: 0, total_movies: 0, total_investments: 0, total_posts: 0, total_raised: 0, total_revenue: 0, total_target: 0, investors: 0, producers: 0, fans: 0, industry_breakdown: {} };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />

      <nav data-web-chrome="nav" className="relative z-10 glass-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/')}
              data-testid="admin-nav-home"
              className="flex items-center space-x-3"
            >
              <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
              <span className="px-2 py-0.5 bg-gold/10 border border-gold/30 rounded-sm text-gold text-xs font-bold uppercase">
                Admin
              </span>
            </button>
            <div className="flex items-center space-x-3">
              <button
                data-testid="admin-nav-marketplace"
                onClick={() => navigate('/marketplace')}
                className="btn-secondary hidden sm:inline-flex"
              >
                Marketplace
              </button>
              <button
                data-testid="admin-nav-logout"
                onClick={handleLogout}
                className="btn-secondary flex items-center space-x-2"
              >
                <SignOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-10">
          <h1 className="font-heading text-5xl font-bold mb-2">
            ADMIN <span className="text-gold">CONSOLE</span>
          </h1>
          <p className="font-body text-lg text-slate-400">
            Welcome, {user.name}. Manage the platform.
            {myTier && (
              <span
                data-testid="admin-tier-badge"
                className={`ml-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-xs font-bold uppercase tracking-wide border ${
                  myTier === 'founder'
                    ? 'bg-gold/15 border-gold/40 text-gold'
                    : myTier === 'admin'
                    ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
                    : 'bg-sky-500/10 border-sky-500/40 text-sky-300'
                }`}
              >
                {myTier === 'founder' && <Crown size={12} weight="fill" />}
                {myTier === 'admin' && <Shield size={12} weight="fill" />}
                {myTier === 'account_admin' && <CurrencyDollar size={12} weight="fill" />}
                {myTier === 'founder' ? 'Founder Admin' : myTier === 'admin' ? 'Admin' : 'Account Admin'}
              </span>
            )}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap mb-8 border-b border-white/5 pb-3 native-chip-row" data-testid="admin-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: TrendUp },
            { id: 'approvals', label: `Approvals${pendingMovies.length ? ` (${pendingMovies.length})` : ''}`, icon: Gavel, hideFor: ['account_admin'] },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'movies', label: 'Movies', icon: FilmStrip },
            { id: 'investments', label: 'Investments', icon: CurrencyDollar },
            { id: 'verification', label: 'Producer Verification', icon: SealCheck, hideFor: ['account_admin'] },
            { id: 'admins', label: 'Admin Tiers', icon: Crown, hideFor: ['admin', 'account_admin'] },
            { id: 'founder', label: 'Founder Console', icon: Shield },
            { id: 'logs', label: 'Login Logs', icon: ClockCounterClockwise },
          ]
            .filter((t) => !(t.hideFor && myTier && t.hideFor.includes(myTier)))
            .map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={t.id === 'founder' ? 'admin-founder-tab' : `admin-tab-${t.id}`}
                aria-pressed={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`px-5 py-2.5 rounded-sm font-body text-sm uppercase tracking-wide transition-all flex items-center gap-2 ${
                  tab === t.id
                    ? 'bg-gold text-black font-bold'
                    : 'bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10'
                }`}
              >
                <Icon size={16} weight="bold" />
                {t.label}
              </button>
            );
          })}
        </div>

        {tab === 'approvals' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-approvals-tab"
          >
            <div className="bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/30 rounded-lg p-5 mb-6 flex items-start gap-4">
              <Gavel size={32} className="text-amber-300 flex-shrink-0 mt-1" weight="duotone" />
              <div>
                <h3 className="font-heading text-xl font-bold text-amber-200 mb-1">Producer Project Approvals</h3>
                <p className="font-body text-sm text-slate-300">
                  New movies submitted by producers wait here until you approve them. Rejected
                  movies require a reason which is shown to the producer so they can fix it and
                  resubmit.
                </p>
              </div>
            </div>

            <div className="bg-card border border-white/5 rounded-lg p-6 mb-8">
              <h3 className="font-heading text-2xl font-bold mb-4 flex items-center gap-3">
                Pending
                <span className="px-2 py-0.5 bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm rounded-sm">
                  {pendingMovies.length}
                </span>
              </h3>
              {pendingMovies.length === 0 ? (
                <p className="font-body text-slate-400 text-sm" data-testid="approvals-empty">
                  No movies are waiting for review. Newly submitted projects will land here.
                </p>
              ) : (
                <div className="space-y-3" data-testid="approvals-pending-list">
                  {pendingMovies.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`approval-row-${m.id}`}
                      className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg p-4"
                    >
                      <img
                        src={m.poster_url}
                        alt={m.title}
                        className="w-16 h-20 object-cover rounded-sm border border-white/10"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-lg font-bold truncate">{m.title}</div>
                        <div className="font-body text-xs text-slate-400">
                          {m.industry} · {m.genre} · by {m.producer_name}
                        </div>
                        <div className="font-body text-xs text-slate-400 mt-1 line-clamp-2">{m.description}</div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          data-testid={`approve-btn-${m.id}`}
                          onClick={() => handleApproveMovie(m.id)}
                          className="px-3 py-2 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 rounded-sm font-body text-xs uppercase font-bold flex items-center gap-1.5 hover:bg-emerald-500/25"
                        >
                          <CheckCircle size={16} weight="bold" />
                          Approve
                        </button>
                        <button
                          data-testid={`reject-btn-${m.id}`}
                          onClick={() => { setRejectTarget({ id: m.id, title: m.title }); setRejectReason(''); }}
                          className="px-3 py-2 bg-rose-500/15 border border-rose-500/40 text-rose-300 rounded-sm font-body text-xs uppercase font-bold flex items-center gap-1.5 hover:bg-rose-500/25"
                        >
                          <XCircle size={16} weight="bold" />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-card border border-white/5 rounded-lg p-6">
              <h3 className="font-heading text-2xl font-bold mb-4">Recently Rejected</h3>
              {rejectedMovies.length === 0 ? (
                <p className="font-body text-slate-400 text-sm">No rejected projects.</p>
              ) : (
                <div className="space-y-3">
                  {rejectedMovies.slice(0, 20).map((m) => (
                    <div key={m.id} className="bg-white/5 border border-rose-500/20 rounded-lg p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div>
                          <div className="font-heading text-base font-bold">{m.title}</div>
                          <div className="font-body text-xs text-slate-400">by {m.producer_name}</div>
                        </div>
                        <span className="px-2 py-0.5 bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs font-bold uppercase rounded-sm">
                          Rejected
                        </span>
                      </div>
                      {m.rejection_reason && (
                        <div className="bg-rose-500/5 border border-rose-500/20 rounded-sm p-2.5 font-body text-xs text-rose-200">
                          <span className="font-bold">Reason:</span> {m.rejection_reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {tab === 'admins' && myTier === 'founder' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-admins-tab"
          >
            <div className="bg-gradient-to-r from-gold/10 to-transparent border border-gold/30 rounded-lg p-5 mb-6 flex items-start gap-4">
              <Crown size={32} className="text-gold flex-shrink-0 mt-1" weight="duotone" />
              <div>
                <h3 className="font-heading text-xl font-bold text-gold mb-1">Granular Admin Tiers</h3>
                <p className="font-body text-sm text-slate-300">
                  <span className="text-gold font-bold">Founder Admin</span>: full power, only tier
                  that can promote/demote others. <span className="text-emerald-300 font-bold">Admin</span>:
                  approve user uploads & moderate content, no destructive financial controls.{' '}
                  <span className="text-sky-300 font-bold">Account Admin</span>: read-only access to
                  Zingy / Zinglets / fiat movements.
                </p>
              </div>
            </div>

            <div className="bg-card border border-white/5 rounded-lg p-6 mb-6">
              <h3 className="font-heading text-xl font-bold mb-4">Grant Admin Access</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  data-testid="grant-admin-email"
                  aria-label="Email of the user to make admin"
                  type="email"
                  value={grantEmail}
                  onChange={(e) => setGrantEmail(e.target.value)}
                  placeholder="user@example.com (must be an existing user)"
                  className="flex-1 bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm font-body"
                />
                <select
                  data-testid="grant-admin-tier"
                  aria-label="Admin tier to grant"
                  value={grantTier}
                  onChange={(e) => setGrantTier(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm font-body"
                >
                  <option value="account_admin">Account Admin (read-only finance)</option>
                  <option value="admin">Admin (moderate content)</option>
                  <option value="founder">Founder Admin (full power)</option>
                </select>
                <button
                  data-testid="grant-admin-btn"
                  onClick={handleGrantAdmin}
                  className="btn-primary"
                >
                  Grant
                </button>
              </div>
            </div>

            <div className="bg-card border border-white/5 rounded-lg p-6">
              <h3 className="font-heading text-xl font-bold mb-4">Existing Admins ({allAdmins.length})</h3>
              {allAdmins.length === 0 ? (
                <p className="font-body text-slate-400 text-sm">No admins.</p>
              ) : (
                <div className="space-y-2" data-testid="admins-list">
                  {allAdmins.map((a) => (
                    <div
                      key={a.id}
                      data-testid={`admin-row-${a.id}`}
                      className="flex items-center justify-between gap-3 bg-white/5 border border-white/10 rounded-sm p-3"
                    >
                      <div className="min-w-0">
                        <div className="font-body text-sm font-bold truncate">{a.name}</div>
                        <div className="font-body text-xs text-slate-400 truncate">{a.email}</div>
                      </div>
                      <select
                        data-testid={`tier-select-${a.id}`}
                        aria-label={`Admin tier for ${a.name}`}
                        value={a.admin_tier || 'admin'}
                        onChange={(e) => handleChangeTier(a.id, e.target.value)}
                        className="bg-background border border-white/15 rounded-sm px-2 py-1 text-xs font-body"
                      >
                        <option value="account_admin">Account Admin</option>
                        <option value="admin">Admin</option>
                        <option value="founder">Founder Admin</option>
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {tab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-overview"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Users', val: safeStats.total_users, icon: Users },
                { label: 'Movies', val: safeStats.total_movies, icon: FilmStrip },
                { label: 'Investments', val: safeStats.total_investments, icon: CurrencyDollar },
                { label: 'Community Posts', val: safeStats.total_posts, icon: ChatCircleDots },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="bg-card border border-white/5 rounded-lg p-5">
                    <Icon size={28} className="text-gold mb-3" weight="bold" />
                    <div className="font-body text-xs text-slate-400 mb-1 uppercase">{s.label}</div>
                    <div className="font-mono text-2xl font-bold tabular-nums">{s.val}</div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-card border border-white/5 rounded-lg p-5">
                <div className="font-body text-xs text-slate-400 mb-1 uppercase">Total Raised</div>
                <div className="font-mono text-2xl font-bold text-gold tabular-nums">{fmtUsd(safeStats.total_raised)}</div>
                <div className="font-body text-xs text-slate-400 mt-1">of {fmtUsd(safeStats.total_target)} target</div>
              </div>
              <div className="bg-card border border-white/5 rounded-lg p-5">
                <div className="font-body text-xs text-slate-400 mb-1 uppercase">Total Revenue</div>
                <div className="font-mono text-2xl font-bold text-gold tabular-nums">{fmtUsd(safeStats.total_revenue)}</div>
              </div>
              <div className="bg-card border border-white/5 rounded-lg p-5">
                <div className="font-body text-xs text-slate-400 mb-2 uppercase">Roles</div>
                <div className="space-y-1 text-sm font-body">
                  <div className="flex justify-between"><span className="text-slate-400">Investors</span><span className="font-mono">{safeStats.investors}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Producers</span><span className="font-mono">{safeStats.producers}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Fans</span><span className="font-mono">{safeStats.fans}</span></div>
                </div>
              </div>
            </div>

            <div className="bg-card border border-white/5 rounded-lg p-6">
              <h3 className="font-heading text-xl font-bold mb-4">Movies by Industry</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(safeStats.industry_breakdown || {}).map(([ind, count]) => (
                  <div key={ind} className="bg-white/5 border border-white/10 rounded-sm p-4 text-center">
                    <div className="font-body text-xs text-slate-400 uppercase mb-1">{ind}</div>
                    <div className="font-mono text-2xl font-bold text-gold tabular-nums">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {tab === 'users' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="admin-users-tab">
            <div className="flex justify-end mb-4">
              <button
                data-testid="admin-create-user-btn"
                onClick={() => setShowCreateUser(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={16} weight="bold" />
                Create User
              </button>
            </div>
            <div className="bg-card border border-white/5 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase tracking-wide">Name</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase tracking-wide">Email</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase tracking-wide">Role</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5" data-testid={`admin-user-row-${u.id}`}>
                      <td className="px-5 py-3 font-body">{u.name}</td>
                      <td className="px-5 py-3 font-body text-slate-400 text-sm">{u.email}</td>
                      <td className="px-5 py-3">
                        <select
                          data-testid={`admin-user-role-${u.id}`}
                          aria-label={`Role for ${u.name}`}
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          className="bg-white/5 border border-white/10 rounded-sm px-3 py-1.5 text-sm font-body focus:outline-none focus:border-gold"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex items-center gap-3">
                          <button
                            data-testid={`admin-user-reset-${u.id}`}
                            onClick={() => setResetTarget({ id: u.id, email: u.email })}
                            className="text-gold hover:text-gold/80 inline-flex items-center gap-1 text-sm"
                          >
                            <Key size={16} />
                            Reset PW
                          </button>
                          {u.id !== user.id ? (
                            <button
                              data-testid={`admin-user-delete-${u.id}`}
                              onClick={() => setDeleteTarget({ type: 'user', id: u.id })}
                              className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 text-sm"
                            >
                              <Trash size={16} />
                              Delete
                            </button>
                          ) : (
                            <span className="text-xs text-slate-400 uppercase">You</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {tab === 'movies' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="admin-movies-tab">
            <div className="flex justify-end mb-4">
              <button
                data-testid="admin-create-movie-btn"
                onClick={() => setShowCreateMovie(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={16} weight="bold" />
                Upload Movie
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {movies.map((m) => {
                const pct = Math.min((m.raised_amount / m.target_amount) * 100, 100);
                return (
                  <div
                    key={m.id}
                    className="bg-card border border-white/5 rounded-lg p-5"
                    data-testid={`admin-movie-${m.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-heading text-lg font-bold">{m.title}</h4>
                        <div className="text-xs text-slate-400 uppercase tracking-wide">
                          {m.industry} • {m.status}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          data-testid={`admin-movie-edit-${m.id}`}
                          onClick={() => setEditMovie(m)}
                          className="text-gold hover:text-gold/80 text-sm font-bold uppercase tracking-wide"
                        >
                          Edit
                        </button>
                        <button aria-label="Delete movie"
                          data-testid={`admin-movie-delete-${m.id}`}
                          onClick={() => setDeleteTarget({ type: 'movie', id: m.id })}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 line-clamp-2 mb-3">{m.description}</p>
                    <div className="flex justify-between text-sm font-mono mb-1">
                      <span className="text-slate-400">Raised</span>
                      <span className="text-gold">{fmtUsd(m.raised_amount)} / {fmtUsd(m.target_amount)}</span>
                    </div>
                    <div className="w-full bg-white/5 rounded-full h-1.5">
                      <div className="bg-gold h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="text-xs text-slate-400 mt-2">Producer: {m.producer_name}</div>
                  </div>
                );
              })}
              {movies.length === 0 && (
                <div className="col-span-full text-center text-slate-400 py-12 font-body">No movies yet</div>
              )}
            </div>
          </motion.div>
        )}

        {tab === 'investments' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="admin-investments-tab">
            <div className="bg-card border border-white/5 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Investor</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Movie</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Tier</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Amount</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Royalty %</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((inv) => (
                    <tr key={inv.id} className="border-t border-white/5" data-testid={`admin-inv-${inv.id}`}>
                      <td className="px-5 py-3 font-body">{inv.user_name}</td>
                      <td className="px-5 py-3 font-body text-slate-400">{inv.movie_title}</td>
                      <td className="px-5 py-3 font-body text-slate-400 text-sm">{inv.tier_name}</td>
                      <td className="px-5 py-3 text-right font-mono text-gold">{fmtUsd(inv.amount)}</td>
                      <td className="px-5 py-3 text-right font-mono">{inv.royalty_percentage}%</td>
                      <td className="px-5 py-3 text-right text-xs text-slate-400">
                        {new Date(inv.investment_date).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                  {investments.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-slate-400 py-12 font-body">No investments yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
        {tab === 'verification' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="admin-verification-tab">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <h2 className="font-heading text-2xl font-bold">Producer Project Verification</h2>
              <span className="text-xs text-slate-400 font-body">
                {producerProjects.filter(p => p.status === 'pending').length} pending · {producerProjects.filter(p => p.status === 'verified').length} verified · {producerProjects.filter(p => p.status === 'rejected').length} rejected
              </span>
            </div>
            <p className="font-body text-sm text-slate-400 mb-4">
              Review producer-submitted previous projects. Verified projects are visible on the producer's public profile and feed into ROI / on-time stats for investors.
            </p>

            {/* PAPPA verification block */}
            <div className="mb-8" data-testid="admin-pappa-block">
              <h3 className="font-heading text-lg font-bold mb-2">PAPPA Purchase-Order Verification <span className="text-xs text-slate-400 font-body">({pappaPending.length} pending)</span></h3>
              {pappaPending.length === 0 ? (
                <p className="font-body text-sm text-slate-400 mb-4">No PAPPA tracks pending verification.</p>
              ) : (
                <div className="bg-card border border-white/5 rounded-lg overflow-x-auto mb-4">
                  <table className="w-full min-w-[800px]">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Movie</th>
                        <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Producer</th>
                        <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">OTT partner</th>
                        <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">PO → raise → markup</th>
                        <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">PO doc</th>
                        <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pappaPending.map(m => (
                        <tr key={m.movie_id} className="border-t border-white/5" data-testid={`pappa-row-${m.movie_id}`}>
                          <td className="px-5 py-3 font-body text-sm">
                            <div className="font-bold">{m.movie_title}</div>
                            <div className="text-[10px] text-slate-400">{m.industry}</div>
                          </td>
                          <td className="px-5 py-3 font-body text-xs">{m.producer_name}</td>
                          <td className="px-5 py-3 font-body text-sm font-bold">{m.pappa.ott_partner_name}</td>
                          <td className="px-5 py-3 font-mono text-xs tabular-nums">
                            ₹{formatInrFriendly(m.pappa.ott_po_amount_inr)} → ₹{formatInrFriendly(m.pappa.raise_amount_inr)} <span className="text-emerald-400">+{m.pappa.markup_pct}%</span>
                          </td>
                          <td className="px-5 py-3 font-body text-xs">
                            {m.pappa.po_doc_url ? (
                              <a href={`${BACKEND_URL}${m.pappa.po_doc_url}`} target="_blank" rel="noreferrer" className="text-gold hover:underline" data-testid={`pappa-po-link-${m.movie_id}`}>View PO ↗</a>
                            ) : <span className="text-slate-400">No doc</span>}
                          </td>
                          <td className="px-5 py-3 text-right whitespace-nowrap">
                            <button
                              data-testid={`pappa-approve-${m.movie_id}`}
                              onClick={async () => {
                                try {
                                  await axios.post(`${BACKEND_URL}/api/admin/movies/${m.movie_id}/pappa/verify`, { status: 'active' }, authHeaders());
                                  toast.success('PAPPA verified');
                                  loadAll();
                                } catch (err) { toast.error('Failed'); }
                              }}
                              className="text-xs px-3 py-1.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 mr-1 transition-colors"
                            >Approve</button>
                            <button
                              data-testid={`pappa-reject-${m.movie_id}`}
                              onClick={() => { setVerifyTarget({ kind: 'pappa', movie_id: m.movie_id, title: m.movie_title }); setVerifyNotes(''); }}
                              className="text-xs px-3 py-1.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-200 hover:bg-rose-500/25 transition-colors"
                            >Reject</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <h3 className="font-heading text-lg font-bold mb-2 mt-6">Previous-Project Verification</h3>
            <div className="bg-card border border-white/5 rounded-lg overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Title</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Producer</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Industry / Role</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Budget → Box</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">ROI</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Status</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {producerProjects.length === 0 && (
                    <tr><td colSpan={7} className="text-center text-slate-400 py-8 font-body text-sm">No producer projects submitted yet.</td></tr>
                  )}
                  {producerProjects.map(p => (
                    <tr key={p.id} className="border-t border-white/5" data-testid={`verify-row-${p.id}`}>
                      <td className="px-5 py-3 font-body">
                        <div className="font-bold">{p.title}</div>
                        <div className="text-[10px] text-slate-400">{p.release_date}</div>
                        <div className="flex gap-2 mt-1">
                          {p.imdb_url && <a href={p.imdb_url} target="_blank" rel="noreferrer" className="text-[10px] text-gold hover:underline">IMDb</a>}
                          {p.trailer_url && <a href={p.trailer_url} target="_blank" rel="noreferrer" className="text-[10px] text-gold hover:underline">Trailer</a>}
                          {p.proof_doc_url && <a href={`${BACKEND_URL}${p.proof_doc_url}`} target="_blank" rel="noreferrer" className="text-[10px] text-gold hover:underline">Proof doc</a>}
                        </div>
                      </td>
                      <td className="px-5 py-3 font-body text-xs">
                        <div>{p.producer_name}</div>
                        <div className="text-slate-400">{p.producer_email}</div>
                      </td>
                      <td className="px-5 py-3 font-body text-xs">{p.industry}<br/><span className="text-slate-400">{p.role}</span></td>
                      <td className="px-5 py-3 font-mono text-xs tabular-nums">₹{formatInrFriendly(p.budget_inr)} → ₹{formatInrFriendly(p.box_office_inr)}</td>
                      <td className={`px-5 py-3 font-mono text-sm font-bold tabular-nums ${p.roi_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{p.roi_pct > 0 ? '+' : ''}{p.roi_pct}%</td>
                      <td className="px-5 py-3">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
                          p.status === 'verified' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' :
                          p.status === 'rejected' ? 'bg-rose-500/15 text-rose-300 border border-rose-500/30' :
                          'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                        }`}>{p.status}</span>
                        {p.review_notes && <div className="text-[10px] text-slate-400 mt-1 max-w-[200px]">{p.review_notes}</div>}
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        {p.status !== 'verified' && (
                          <button
                            data-testid={`approve-${p.id}`}
                            onClick={async () => {
                              try {
                                await axios.post(`${BACKEND_URL}/api/admin/producer-projects/${p.id}/verify`, { status: 'verified' }, authHeaders());
                                toast.success('Verified');
                                loadAll();
                              } catch (err) { toast.error('Failed'); }
                            }}
                            className="text-xs px-3 py-1.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 mr-1 transition-colors"
                          >Approve</button>
                        )}
                        {p.status !== 'rejected' && (
                          <button
                            data-testid={`reject-${p.id}`}
                            onClick={() => { setVerifyTarget({ ...p, kind: 'project' }); setVerifyNotes(''); }}
                            className="text-xs px-3 py-1.5 rounded bg-rose-500/15 border border-rose-500/40 text-rose-200 hover:bg-rose-500/25 transition-colors"
                          >Reject</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {tab === 'founder' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="admin-founder-content">
            <h2 className="font-heading text-2xl font-bold mb-2">Founder Console</h2>
            <p className="font-body text-sm text-slate-400 mb-6">Money-flow oversight: where Zingys live, which investors hold them, how much is in each project's escrow, and what's pending redemption.</p>

            {founderOverview && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="founder-overview-grid">
                <div className="bg-card border border-purple-500/30 rounded p-4">
                  <div className="text-[10px] uppercase text-slate-400">Zingy in wallets</div>
                  <div className="font-mono text-xl font-bold tabular-nums text-gold">{(Math.round(founderOverview.zingy_in_wallets * 100) / 100).toLocaleString('en-IN')}</div>
                  <div className="text-[10px] text-slate-400 mt-1">= ₹{founderOverview.zingy_in_wallets_inr.toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-card border border-cyan-500/30 rounded p-4">
                  <div className="text-[10px] uppercase text-slate-400">FUNDER in escrow</div>
                  <div className="font-mono text-xl font-bold tabular-nums text-cyan-300">₹{founderOverview.funder_in_escrow_inr.toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-card border border-amber-500/30 rounded p-4">
                  <div className="text-[10px] uppercase text-slate-400">PAPPA in escrow</div>
                  <div className="font-mono text-xl font-bold tabular-nums text-amber-300">₹{founderOverview.pappa_in_escrow_inr.toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-card border border-emerald-500/30 rounded p-4">
                  <div className="text-[10px] uppercase text-slate-400">PAPPA disbursed (lifetime)</div>
                  <div className="font-mono text-xl font-bold tabular-nums text-emerald-300">₹{founderOverview.pappa_disbursed_inr.toLocaleString('en-IN')}</div>
                </div>
                <div className="bg-card border border-rose-500/30 rounded p-4 md:col-span-2">
                  <div className="text-[10px] uppercase text-slate-400">Pending redemptions</div>
                  <div className="font-mono text-xl font-bold tabular-nums text-rose-300">₹{founderOverview.pending_redemption_inr.toLocaleString('en-IN')} <span className="text-xs text-slate-400">({founderOverview.pending_redemption_count} requests)</span></div>
                </div>
                <div className="bg-card border border-white/10 rounded p-4 md:col-span-2">
                  <div className="text-[10px] uppercase text-slate-400">Active users · wallets</div>
                  <div className="font-mono text-xl font-bold tabular-nums">{founderOverview.users_count} <span className="text-xs text-slate-400">users · {founderOverview.wallets_count} wallets</span></div>
                </div>
              </div>
            )}

            <h3 className="font-heading text-lg font-bold mb-2">Investors ({founderInvestors.length})</h3>
            <div className="bg-card border border-white/5 rounded-lg overflow-x-auto mb-8">
              <table className="w-full min-w-[900px]" data-testid="founder-investors-table">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Investor</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Balance</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Lifetime credited</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Lifetime debited</th>
                    <th className="text-center px-5 py-3 font-body text-xs text-slate-400 uppercase">Auto-reinvest</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {founderInvestors.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-8 font-body text-sm">No Zingy activity yet on the platform.</td></tr>}
                  {founderInvestors.map(r => (
                    <tr key={r.user_id} className="border-t border-white/5" data-testid={`founder-investor-${r.user_id}`}>
                      <td className="px-5 py-3 font-body text-sm">
                        <div className="font-bold">{r.name}</div>
                        <div className="text-[10px] text-slate-400">{r.email}</div>
                      </td>
                      <td className="px-5 py-3 font-mono text-sm tabular-nums text-right">
                        {(Math.round(r.balance_zingy * 100) / 100).toLocaleString('en-IN')} Z
                        <div className="text-[10px] text-slate-400">₹{r.balance_inr.toLocaleString('en-IN')}</div>
                      </td>
                      <td className="px-5 py-3 font-mono text-sm tabular-nums text-emerald-300 text-right">+₹{r.lifetime_credited_inr.toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 font-mono text-sm tabular-nums text-rose-300 text-right">−₹{r.lifetime_debited_inr.toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 text-center">
                        {r.autoreinvest_enabled
                          ? <span className="text-[10px] uppercase font-bold tracking-wide bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded px-2 py-0.5">ON · {r.autoreinvest_allocation_count}</span>
                          : <span className="text-[10px] uppercase tracking-wide text-slate-400">off</span>}
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button
                          data-testid={`founder-investor-drill-${r.user_id}`}
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${BACKEND_URL}/api/admin/founder/investors/${r.user_id}`, authHeaders());
                              setInvestorDetail(res.data);
                            } catch (err) { toast.error('Could not load detail'); }
                          }}
                          className="text-xs px-3 py-1 rounded bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                        >Drill ↗</button>
                        <a
                          href={`${BACKEND_URL}/api/admin/founder/investors/${r.user_id}/export.csv`}
                          target="_blank" rel="noreferrer"
                          onClick={(e) => {
                            // Use fetch to attach the bearer token, then download
                            e.preventDefault();
                            fetch(`${BACKEND_URL}/api/admin/founder/investors/${r.user_id}/export.csv`, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            }).then(res => res.blob()).then(b => {
                              const url = URL.createObjectURL(b);
                              const a = document.createElement('a');
                              a.href = url; a.download = `investor-${r.user_id.slice(0,8)}-zingy.csv`; a.click();
                              URL.revokeObjectURL(url);
                            });
                          }}
                          data-testid={`founder-investor-csv-${r.user_id}`}
                          className="ml-1 text-xs px-3 py-1 rounded bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 transition-colors inline-block"
                        >CSV</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h3 className="font-heading text-lg font-bold mb-2">Per-movie cashflow</h3>
            <div className="bg-card border border-white/5 rounded-lg overflow-x-auto">
              <table className="w-full min-w-[700px]" data-testid="founder-movies-table">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Movie</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Modes</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">FUNDER raised</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">PAPPA raised</th>
                    <th className="text-right px-5 py-3 font-body text-xs text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {movies.map(m => (
                    <tr key={m.id} className="border-t border-white/5">
                      <td className="px-5 py-3 font-body text-sm"><div className="font-bold">{m.title}</div><div className="text-[10px] text-slate-400">{m.industry}</div></td>
                      <td className="px-5 py-3 text-[10px] text-slate-300 font-body">{(m.funding_modes || []).join(', ') || '—'}</td>
                      <td className="px-5 py-3 font-mono text-sm tabular-nums text-right">₹{(m.funder?.raised_amount_inr || m.raised_amount || 0).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 font-mono text-sm tabular-nums text-right">₹{(m.pappa?.raised_amount_inr || 0).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <button
                          data-testid={`founder-movie-drill-${m.id}`}
                          onClick={async () => {
                            try {
                              const res = await axios.get(`${BACKEND_URL}/api/admin/founder/movies/${m.id}/cashflow`, authHeaders());
                              setMovieCashflow(res.data);
                            } catch (err) { toast.error('Could not load cashflow'); }
                          }}
                          className="text-xs px-3 py-1 rounded bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                        >Drill ↗</button>
                        <button
                          data-testid={`founder-movie-csv-${m.id}`}
                          onClick={() => {
                            fetch(`${BACKEND_URL}/api/admin/founder/movies/${m.id}/cashflow/export.csv`, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            }).then(res => res.blob()).then(b => {
                              const url = URL.createObjectURL(b);
                              const a = document.createElement('a');
                              a.href = url; a.download = `movie-${m.id.slice(0,8)}-cashflow.csv`; a.click();
                              URL.revokeObjectURL(url);
                            });
                          }}
                          className="ml-1 text-xs px-3 py-1 rounded bg-amber-500/15 border border-amber-500/30 text-amber-200 hover:bg-amber-500/25 transition-colors"
                        >CSV</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {tab === 'logs' && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="admin-logs-tab">
            <p className="font-body text-sm text-slate-400 mb-4">
              Showing the most recent {loginLogs.length} login attempts (successes and failures).
            </p>
            <div className="bg-card border border-white/5 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">When</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Email</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">Result</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">IP</th>
                    <th className="text-left px-5 py-3 font-body text-xs text-slate-400 uppercase">User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {loginLogs.map((log) => (
                    <tr key={log.id} className="border-t border-white/5" data-testid={`admin-log-${log.id}`}>
                      <td className="px-5 py-3 font-mono text-xs text-slate-400 whitespace-nowrap">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 font-body text-sm">{log.email}</td>
                      <td className="px-5 py-3">
                        {log.success ? (
                          <span className="inline-flex items-center gap-1 text-emerald-400 text-sm">
                            <CheckCircle size={14} weight="fill" /> Success
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-400 text-sm">
                            <XCircle size={14} weight="fill" /> Failed
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-slate-400">{log.ip || '—'}</td>
                      <td className="px-5 py-3 text-xs text-slate-400 max-w-xs truncate" title={log.user_agent}>
                        {log.user_agent || '—'}
                      </td>
                    </tr>
                  ))}
                  {loginLogs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-slate-400 py-12 font-body">No login activity yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>

      {showCreateUser && (
        <CreateUserModal onClose={() => setShowCreateUser(false)} onSubmit={handleCreateUser} />
      )}
      {showCreateMovie && (
        <CreateMovieModal onClose={() => setShowCreateMovie(false)} onSubmit={handleCreateMovie} />
      )}
      {editMovie && (
        <EditMovieModal
          movie={editMovie}
          onClose={() => setEditMovie(null)}
          onSubmit={(data) => handleEditMovie(editMovie.id, data)}
        />
      )}
      {resetTarget && (
        <ResetPasswordModal
          email={resetTarget.email}
          onClose={() => setResetTarget(null)}
          onSubmit={handleResetPassword}
        />
      )}
      {rejectTarget && (
        <Modal onClose={() => { setRejectTarget(null); setRejectReason(''); }} title={`Reject "${rejectTarget.title}"`}>
          <div className="bg-card border border-rose-500/30 rounded-lg w-full max-w-md p-6 relative">
            <button aria-label="Close dialog"
              onClick={() => { setRejectTarget(null); setRejectReason(''); }}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X size={22} />
            </button>
            <h2 className="font-heading text-2xl font-bold mb-2">Reject &quot;{rejectTarget.title}&quot;</h2>
            <p className="font-body text-sm text-slate-400 mb-4">
              The producer will see this reason on their dashboard. Be specific so they can fix and resubmit.
            </p>
            <textarea
              data-testid="reject-reason-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={5}
              maxLength={1000}
              placeholder="e.g., Poster resolution is too low. Please upload at least 800×1200px. Also missing crew bios."
              className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm font-body resize-y"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setRejectTarget(null); setRejectReason(''); }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                data-testid="confirm-reject-btn"
                onClick={handleRejectMovie}
                className="px-4 py-2 bg-rose-500/20 border border-rose-500/40 text-rose-200 rounded-sm font-body text-sm uppercase font-bold hover:bg-rose-500/30"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        </Modal>
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.type === 'user' ? 'Delete this user permanently?' : 'Remove this movie?'}
            </AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteTarget?.type === 'user'
                  ? handleDeleteUser(deleteTarget.id)
                  : handleDeleteMovie(deleteTarget.id)
              }
            >
              {deleteTarget?.type === 'user' ? 'Delete' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {investorDetail && (
        <Modal onClose={() => setInvestorDetail(null)} title={investorDetail.user.name}>
          <div className="bg-card border border-white/10 rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 relative">
            <button aria-label="Close dialog" onClick={() => setInvestorDetail(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={22} /></button>
            <h2 className="font-heading text-2xl font-bold mb-1">{investorDetail.user.name}</h2>
            <p className="font-body text-xs text-slate-400 mb-4">{investorDetail.user.email} · {investorDetail.user.role}</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className="bg-white/[0.03] border border-white/10 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Wallet balance</div>
                <div className="font-mono text-base font-bold tabular-nums text-gold">{(Math.round(investorDetail.balance_zingy * 100) / 100).toLocaleString('en-IN')} Z</div>
                <div className="text-[10px] text-slate-400">₹{investorDetail.balance_inr.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Lifetime credited</div>
                <div className="font-mono text-base font-bold tabular-nums text-emerald-300">+₹{investorDetail.lifetime_credited_inr.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Lifetime debited</div>
                <div className="font-mono text-base font-bold tabular-nums text-rose-300">−₹{investorDetail.lifetime_debited_inr.toLocaleString('en-IN')}</div>
              </div>
              <div className="bg-white/[0.03] border border-white/10 rounded p-3">
                <div className="text-[10px] uppercase text-slate-400">Auto-reinvest</div>
                <div className="font-body text-sm">{investorDetail.autoreinvest_rule?.enabled ? <span className="text-emerald-300 font-bold">ON · {investorDetail.autoreinvest_rule.allocations.length} allocs</span> : <span className="text-slate-400">off</span>}</div>
              </div>
            </div>

            <h3 className="font-heading text-sm font-bold uppercase mb-2 text-slate-300">Ledger ({investorDetail.transactions.length})</h3>
            <div className="bg-black/30 rounded border border-white/5 max-h-72 overflow-y-auto mb-4" data-testid="investor-ledger">
              {investorDetail.transactions.map(t => (
                <div key={t.id} className="px-4 py-2 border-b border-white/5 last:border-0 grid grid-cols-12 gap-2 items-center text-xs">
                  <div className="col-span-3 text-slate-400 font-mono">{new Date(t.created_at).toLocaleString()}</div>
                  <div className={`col-span-1 font-bold uppercase text-[10px] ${t.type === 'credit' ? 'text-emerald-300' : 'text-rose-300'}`}>{t.type}</div>
                  <div className="col-span-3 text-slate-300 font-body">{t.source.replace(/_/g, ' ')}</div>
                  <div className="col-span-3 text-slate-400 font-body truncate" title={t.ref_movie_title || ''}>{t.ref_movie_title || ''}</div>
                  <div className={`col-span-2 font-mono tabular-nums text-right ${t.type === 'credit' ? 'text-emerald-300' : 'text-rose-300'}`}>{t.type === 'credit' ? '+' : '−'}₹{t.amount_inr.toLocaleString('en-IN')}</div>
                </div>
              ))}
              {investorDetail.transactions.length === 0 && <div className="px-4 py-6 text-center text-slate-400 text-xs">No transactions</div>}
            </div>

            {investorDetail.autoreinvest_failures && investorDetail.autoreinvest_failures.length > 0 && (
              <>
                <h3 className="font-heading text-sm font-bold uppercase mb-2 text-amber-300">Auto-reinvest failures ({investorDetail.autoreinvest_failures.length})</h3>
                <div className="bg-amber-500/[0.05] border border-amber-500/20 rounded p-3 mb-4 text-xs space-y-1">
                  {investorDetail.autoreinvest_failures.slice(0, 5).map(f => (
                    <div key={f.id} className="text-amber-200 font-body">• {f.allocation.percentage}% → {f.allocation.target_track} on movie {String(f.allocation.target_movie_id).slice(0, 8)}: {f.reason}</div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {movieCashflow && (
        <Modal onClose={() => setMovieCashflow(null)} title={movieCashflow.movie.title}>
          <div className="bg-card border border-white/10 rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6 relative">
            <button aria-label="Close dialog" onClick={() => setMovieCashflow(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={22} /></button>
            <h2 className="font-heading text-2xl font-bold mb-1">{movieCashflow.movie.title}</h2>
            <p className="font-body text-xs text-slate-400 mb-4">{movieCashflow.movie.industry} · {movieCashflow.movie.producer_name} · modes: {(movieCashflow.movie.funding_modes || []).join(', ') || '—'}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              {['FUNDER', 'PAPPA', 'KUBERA'].map(k => (
                <div key={k} className="bg-white/[0.03] border border-white/10 rounded p-3">
                  <div className="text-[10px] uppercase text-slate-400">{k} inflow</div>
                  <div className="font-mono text-base font-bold tabular-nums">₹{(movieCashflow.by_track[k]?.total_inr || 0).toLocaleString('en-IN')}</div>
                  <div className="text-[10px] text-slate-400">{movieCashflow.by_track[k]?.count || 0} contributions</div>
                </div>
              ))}
            </div>

            <h3 className="font-heading text-sm font-bold uppercase mb-2 text-slate-300">Investments ({movieCashflow.investments.length})</h3>
            <div className="bg-black/30 rounded border border-white/5 max-h-64 overflow-y-auto mb-4">
              {movieCashflow.investments.map(i => (
                <div key={i.id} className="px-4 py-2 border-b border-white/5 last:border-0 grid grid-cols-12 gap-2 items-center text-xs">
                  <div className="col-span-3 text-slate-400 font-mono">{i.investment_date ? new Date(i.investment_date).toLocaleDateString() : ''}</div>
                  <div className="col-span-3 text-slate-200 font-body">{i.user_name}</div>
                  <div className="col-span-2 text-[10px] font-bold uppercase">{i.track_type} · {i.tier_name}</div>
                  <div className="col-span-2 text-[10px] text-slate-400">{i.escrow_status}</div>
                  <div className="col-span-2 font-mono tabular-nums text-right">₹{Number(i.amount).toLocaleString('en-IN')}</div>
                </div>
              ))}
              {movieCashflow.investments.length === 0 && <div className="px-4 py-6 text-center text-slate-400 text-xs">No investments yet</div>}
            </div>

            {movieCashflow.settlements.length > 0 && (
              <>
                <h3 className="font-heading text-sm font-bold uppercase mb-2 text-emerald-300">Settlements ({movieCashflow.settlements.length})</h3>
                <div className="space-y-2">
                  {movieCashflow.settlements.map(s => (
                    <div key={s.id} className="bg-emerald-500/[0.05] border border-emerald-500/20 rounded p-3 text-xs">
                      <div className="flex justify-between"><span className="font-body text-emerald-200">Settled {s.settled_at?.slice(0, 10)}</span><span className="font-mono">OTT paid ₹{s.ott_received_inr.toLocaleString('en-IN')}</span></div>
                      <div className="text-slate-400 mt-1">Distributed: principal ₹{s.total_principal_inr.toLocaleString('en-IN')} + markup ₹{s.total_markup_inr.toLocaleString('en-IN')} · producer kept ₹{s.producer_keep_inr.toLocaleString('en-IN')}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {verifyTarget && (
        <Modal onClose={() => setVerifyTarget(null)} title="Reject verification">
          <div className="bg-card border border-white/10 rounded-lg w-full max-w-md p-7 relative" data-testid="reject-modal">
            <button aria-label="Close dialog" onClick={() => setVerifyTarget(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
              <X size={22} />
            </button>
            <h2 className="font-heading text-2xl font-bold mb-2">Reject {verifyTarget.kind === 'pappa' ? 'PAPPA' : 'project'}</h2>
            <p className="font-body text-sm text-slate-400 mb-4">Provide a reason — visible to the producer so they can correct and resubmit.</p>
            <p className="font-body text-sm font-bold mb-2">{verifyTarget.title}</p>
            <textarea
              data-testid="reject-notes-input"
              rows={4}
              className="admin-input"
              value={verifyNotes}
              onChange={e => setVerifyNotes(e.target.value)}
              placeholder={verifyTarget.kind === 'pappa' ? "e.g., PO document is unreadable / partner not found..." : "e.g., IMDb URL doesn't match, please attach BO India report..."}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setVerifyTarget(null)} className="btn-secondary text-sm py-2">Cancel</button>
              <button
                data-testid="confirm-reject-btn"
                onClick={async () => {
                  try {
                    if (verifyTarget.kind === 'pappa') {
                      await axios.post(`${BACKEND_URL}/api/admin/movies/${verifyTarget.movie_id}/pappa/verify`, { status: 'rejected', review_notes: verifyNotes }, authHeaders());
                    } else {
                      await axios.post(`${BACKEND_URL}/api/admin/producer-projects/${verifyTarget.id}/verify`, { status: 'rejected', review_notes: verifyNotes }, authHeaders());
                    }
                    toast.success('Rejected');
                    setVerifyTarget(null);
                    loadAll();
                  } catch (err) { toast.error('Failed'); }
                }}
                className="px-4 py-2 text-sm rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-200 hover:bg-rose-500/30 transition-colors"
              >Reject</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

const CreateUserModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'investor' });
  return (
    <Modal onClose={onClose} title="Create user">
      <div
        data-testid="admin-create-user-modal"
        className="bg-card border border-white/10 rounded-lg w-full max-w-md p-7 relative"
      >
        <button aria-label="Close dialog" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X size={22} />
        </button>
        <h2 className="font-heading text-2xl font-bold mb-5">
          CREATE <span className="text-gold">USER</span>
        </h2>
        <div className="space-y-3">
          <Field label="Name">
            <input
              data-testid="admin-cu-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="admin-input"
              placeholder="Jane Doe"
            />
          </Field>
          <Field label="Email">
            <input
              data-testid="admin-cu-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="admin-input"
              placeholder="jane@example.com"
            />
          </Field>
          <Field label="Password">
            <input
              data-testid="admin-cu-password"
              type="text"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="admin-input"
              placeholder="At least 4 chars"
            />
          </Field>
          <Field label="Role">
            <select
              data-testid="admin-cu-role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="admin-input"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>
          <button
            data-testid="admin-cu-submit"
            onClick={() => onSubmit(form)}
            disabled={!form.email || !form.password || !form.name}
            className="btn-primary w-full mt-2 disabled:opacity-50"
          >
            Create User
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ResetPasswordModal = ({ email, onClose, onSubmit }) => {
  const [pw, setPw] = useState('');
  return (
    <Modal onClose={onClose} title="Reset password">
      <div
        data-testid="admin-reset-pw-modal"
        className="bg-card border border-white/10 rounded-lg w-full max-w-md p-7 relative"
      >
        <button aria-label="Close dialog" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X size={22} />
        </button>
        <h2 className="font-heading text-2xl font-bold mb-3">
          RESET <span className="text-gold">PASSWORD</span>
        </h2>
        <p className="font-body text-sm text-slate-400 mb-5">
          For <span className="text-white font-bold">{email}</span>
        </p>
        <Field label="New Password">
          <input
            data-testid="admin-rp-input"
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="admin-input"
            placeholder="At least 4 characters"
            autoFocus
          />
        </Field>
        <button
          data-testid="admin-rp-submit"
          onClick={() => onSubmit(pw)}
          disabled={pw.length < 4}
          className="btn-primary w-full mt-4 disabled:opacity-50"
        >
          Reset Password
        </button>
      </div>
    </Modal>
  );
};

const CreateMovieModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    genre: '',
    industry: 'Hollywood',
    budget: '',
    target_amount: '',
    poster_url: '',
    investment_tiers: [
      { name: 'Bronze', min_amount: 1000, royalty_percentage: 0.5, description: 'Entry-level backer' },
      { name: 'Silver', min_amount: 5000, royalty_percentage: 1.5, description: 'Mid-tier backer' },
      { name: 'Gold', min_amount: 25000, royalty_percentage: 5, description: 'Premium executive producer' },
    ],
  });
  const updateTier = (i, key, val) => {
    setForm((p) => ({
      ...p,
      investment_tiers: p.investment_tiers.map((t, idx) => (idx === i ? { ...t, [key]: val } : t)),
    }));
  };
  const submit = () => {
    const payload = {
      ...form,
      budget: parseInrInput(form.budget) || 0,
      target_amount: parseInrInput(form.target_amount) || 0,
      investment_tiers: form.investment_tiers.map((t) => ({
        ...t,
        min_amount: parseInrInput(t.min_amount) || 0,
        royalty_percentage: parseFloat(t.royalty_percentage) || 0,
      })),
    };
    onSubmit(payload);
  };
  return (
    <Modal onClose={onClose} title="Upload movie">
      <div
        data-testid="admin-create-movie-modal"
        className="bg-card border border-white/10 rounded-lg w-full max-w-2xl p-7 relative my-8"
      >
        <button aria-label="Close dialog" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X size={22} />
        </button>
        <h2 className="font-heading text-2xl font-bold mb-5">
          UPLOAD <span className="text-gold">MOVIE</span>
        </h2>
        <div className="space-y-3">
          <Field label="Title">
            <input
              data-testid="admin-cm-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="admin-input"
            />
          </Field>
          <Field label="Description">
            <textarea
              data-testid="admin-cm-description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="admin-input resize-none"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Genre">
              <input
                data-testid="admin-cm-genre"
                value={form.genre}
                onChange={(e) => setForm({ ...form, genre: e.target.value })}
                placeholder="Action / Drama / ..."
                className="admin-input"
              />
            </Field>
            <Field label="Industry">
              <select
                data-testid="admin-cm-industry"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="admin-input"
              >
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Budget (₹)">
              <input
                data-testid="admin-cm-budget"
                type="text"
                inputMode="numeric"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                className="admin-input"
                placeholder="e.g. 50000000  or  5cr"
              />
              {form.budget && (
                <p className="text-xs text-gold mt-1 font-mono">
                  = ₹{parseInrInput(form.budget).toLocaleString('en-IN')} ({formatInrFriendly(parseInrInput(form.budget))})
                </p>
              )}
            </Field>
            <Field label="Target Amount (₹)">
              <input
                data-testid="admin-cm-target"
                type="text"
                inputMode="numeric"
                value={form.target_amount}
                onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                className="admin-input"
                placeholder="e.g. 10000000  or  1cr"
              />
              {form.target_amount && (
                <p className="text-xs text-gold mt-1 font-mono">
                  = ₹{parseInrInput(form.target_amount).toLocaleString('en-IN')} ({formatInrFriendly(parseInrInput(form.target_amount))})
                </p>
              )}
            </Field>
          </div>
          <Field label="Poster">
            <PosterUploader
              value={form.poster_url}
              onChange={(url) => setForm({ ...form, poster_url: url })}
              testIdPrefix="admin-cm"
            />
          </Field>

          <div className="pt-2">
            <div className="font-body text-xs uppercase tracking-wide text-slate-400 mb-2">
              Investment Tiers
            </div>
            <div className="space-y-2">
              {form.investment_tiers.map((t, i) => (
                <div key={`tier-${i}`} className="grid grid-cols-12 gap-2" data-testid={`admin-cm-tier-${i}`}>
                  <input
                    data-testid={`admin-cm-tier-${i}-name`}
                    value={t.name}
                    onChange={(e) => updateTier(i, 'name', e.target.value)}
                    placeholder="Tier name"
                    className="admin-input col-span-3"
                  />
                  <input
                    data-testid={`admin-cm-tier-${i}-min`}
                    type="number"
                    value={t.min_amount}
                    onChange={(e) => updateTier(i, 'min_amount', e.target.value)}
                    placeholder="Min ₹"
                    className="admin-input col-span-3"
                  />
                  <input
                    data-testid={`admin-cm-tier-${i}-royalty`}
                    type="number"
                    step="0.1"
                    value={t.royalty_percentage}
                    onChange={(e) => updateTier(i, 'royalty_percentage', e.target.value)}
                    placeholder="Royalty %"
                    className="admin-input col-span-2"
                  />
                  <input
                    data-testid={`admin-cm-tier-${i}-description`}
                    value={t.description}
                    onChange={(e) => updateTier(i, 'description', e.target.value)}
                    placeholder="Description"
                    className="admin-input col-span-4"
                  />
                </div>
              ))}
            </div>
          </div>

          <button
            data-testid="admin-cm-submit"
            onClick={submit}
            disabled={!form.title || !form.poster_url || !form.target_amount}
            className="btn-primary w-full mt-4 disabled:opacity-50"
          >
            Upload Movie
          </button>
        </div>
      </div>
    </Modal>
  );
};

// A single input/select/textarea child is tied to the label via id; anything
// else becomes a group named by the label.
const Field = ({ label, children }) => {
  const id = useId();
  const labelCls = 'block font-body text-xs uppercase tracking-wide text-slate-400 mb-1.5';
  const single = Children.count(children) === 1 && isValidElement(children)
    && ['input', 'select', 'textarea'].includes(children.type);
  if (single) {
    return (
      <div>
        <label htmlFor={id} className={labelCls}>{label}</label>
        {cloneElement(children, { id })}
      </div>
    );
  }
  return (
    <div role="group" aria-labelledby={id}>
      <span id={id} className={labelCls}>{label}</span>
      {children}
    </div>
  );
};

const PosterUploader = ({ value, onChange, testIdPrefix }) => {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      const fullUrl = `${BACKEND_URL}${res.data.url}`;
      onChange(fullUrl);
      toast.success('Poster uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          data-testid={`${testIdPrefix}-poster-url`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="admin-input flex-1"
          placeholder="https://... or upload below"
        />
        <button
          type="button"
          data-testid={`${testIdPrefix}-poster-browse`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="btn-secondary px-4 py-2 text-sm whitespace-nowrap disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : '📁 Browse'}
        </button>
        <input
          ref={fileRef}
          data-testid={`${testIdPrefix}-poster-file`}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          onChange={handleFile}
          className="hidden"
        />
      </div>
      {value && (
        <div className="relative">
          <img
            src={value}
            alt="Poster preview"
            className="w-full max-h-48 object-cover rounded-sm border border-white/10"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
            data-testid={`${testIdPrefix}-poster-preview`}
          />
        </div>
      )}
    </div>
  );
};

const EditMovieModal = ({ movie, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    title: movie.title || '',
    description: movie.description || '',
    genre: movie.genre || '',
    industry: movie.industry || 'Hollywood',
    poster_url: movie.poster_url || '',
    status: movie.status || 'funding',
    revenue: movie.revenue || 0,
    target_amount: movie.target_amount || 0,
    budget: movie.budget || 0,
    investment_tiers: (movie.investment_tiers || []).map((t) => ({ ...t })),
  });

  const updateTier = (i, key, val) => {
    setForm((p) => ({
      ...p,
      investment_tiers: p.investment_tiers.map((t, idx) =>
        idx === i ? { ...t, [key]: val } : t
      ),
    }));
  };

  const addTier = () => {
    setForm((p) => ({
      ...p,
      investment_tiers: [
        ...p.investment_tiers,
        { name: '', min_amount: 0, royalty_percentage: 0, description: '' },
      ],
    }));
  };

  const removeTier = (i) => {
    setForm((p) => ({
      ...p,
      investment_tiers: p.investment_tiers.filter((_, idx) => idx !== i),
    }));
  };

  const submit = () => {
    const payload = {
      title: form.title,
      description: form.description,
      genre: form.genre,
      industry: form.industry,
      poster_url: form.poster_url,
      status: form.status,
      revenue: parseInrInput(form.revenue) || 0,
      target_amount: parseInrInput(form.target_amount) || 0,
      budget: parseInrInput(form.budget) || 0,
      investment_tiers: form.investment_tiers.map((t) => ({
        name: t.name,
        min_amount: parseInrInput(t.min_amount) || 0,
        royalty_percentage: parseFloat(t.royalty_percentage) || 0,
        description: t.description || '',
      })),
    };
    onSubmit(payload);
  };

  return (
    <Modal onClose={onClose} title="Edit movie">
      <div
        data-testid="admin-edit-movie-modal"
        className="bg-card border border-white/10 rounded-lg w-full max-w-2xl p-7 relative my-8"
      >
        <button aria-label="Close dialog" onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X size={22} />
        </button>
        <h2 className="font-heading text-2xl font-bold mb-5">
          EDIT <span className="text-gold">MOVIE</span>
        </h2>
        <div className="space-y-3">
          <Field label="Title">
            <input
              data-testid="admin-em-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="admin-input"
            />
          </Field>
          <Field label="Description">
            <textarea
              data-testid="admin-em-description"
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="admin-input resize-none"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Genre">
              <input
                data-testid="admin-em-genre"
                value={form.genre}
                onChange={(e) => setForm({ ...form, genre: e.target.value })}
                className="admin-input"
              />
            </Field>
            <Field label="Industry">
              <select
                data-testid="admin-em-industry"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="admin-input"
              >
                {INDUSTRIES.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Status">
              <select
                data-testid="admin-em-status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="admin-input"
              >
                {['funding', 'production', 'released', 'completed'].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
            <Field label="Target (₹)">
              <input
                data-testid="admin-em-target"
                type="text"
                inputMode="numeric"
                value={form.target_amount}
                onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                className="admin-input"
                placeholder="e.g. 5cr"
              />
              {form.target_amount !== '' && form.target_amount !== 0 && (
                <p className="text-xs text-gold mt-1 font-mono">
                  = ₹{parseInrInput(form.target_amount).toLocaleString('en-IN')}
                </p>
              )}
            </Field>
            <Field label="Revenue (₹)">
              <input
                data-testid="admin-em-revenue"
                type="text"
                inputMode="numeric"
                value={form.revenue}
                onChange={(e) => setForm({ ...form, revenue: e.target.value })}
                className="admin-input"
                placeholder="e.g. 50L"
              />
              {form.revenue !== '' && form.revenue !== 0 && (
                <p className="text-xs text-gold mt-1 font-mono">
                  = ₹{parseInrInput(form.revenue).toLocaleString('en-IN')}
                </p>
              )}
            </Field>
          </div>
          <Field label="Poster">
            <PosterUploader
              value={form.poster_url}
              onChange={(url) => setForm({ ...form, poster_url: url })}
              testIdPrefix="admin-em"
            />
          </Field>

          {/* Investment Tier Editor */}
          <div className="pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-body text-xs uppercase tracking-wide text-slate-400">
                Investment Tiers
              </div>
              <button
                type="button"
                data-testid="admin-em-add-tier"
                onClick={addTier}
                className="text-gold text-xs font-bold uppercase tracking-wide hover:text-gold/80"
              >
                + Add Tier
              </button>
            </div>
            <div className="space-y-2" data-testid="admin-em-tiers">
              {form.investment_tiers.map((t, i) => (
                <div key={`em-tier-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`admin-em-tier-${i}`}>
                  <input
                    data-testid={`admin-em-tier-${i}-name`}
                    value={t.name}
                    onChange={(e) => updateTier(i, 'name', e.target.value)}
                    placeholder="Name"
                    className="admin-input col-span-3"
                  />
                  <input
                    data-testid={`admin-em-tier-${i}-min`}
                    type="number"
                    value={t.min_amount}
                    onChange={(e) => updateTier(i, 'min_amount', e.target.value)}
                    placeholder="Min ₹"
                    className="admin-input col-span-2"
                  />
                  <input
                    data-testid={`admin-em-tier-${i}-royalty`}
                    type="number"
                    step="0.1"
                    value={t.royalty_percentage}
                    onChange={(e) => updateTier(i, 'royalty_percentage', e.target.value)}
                    placeholder="Royalty %"
                    className="admin-input col-span-2"
                  />
                  <input
                    data-testid={`admin-em-tier-${i}-description`}
                    value={t.description || ''}
                    onChange={(e) => updateTier(i, 'description', e.target.value)}
                    placeholder="Description"
                    className="admin-input col-span-4"
                  />
                  <button
                    type="button"
                    data-testid={`admin-em-tier-${i}-remove`}
                    onClick={() => removeTier(i)}
                    className="col-span-1 text-red-400 hover:text-red-300 text-xs"
                    aria-label="Remove tier"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              {form.investment_tiers.length === 0 && (
                <div className="text-center text-xs text-slate-400 py-3">
                  No tiers defined. Click + Add Tier above.
                </div>
              )}
            </div>
          </div>

          <button
            data-testid="admin-em-submit"
            onClick={submit}
            disabled={!form.title}
            className="btn-primary w-full mt-2 disabled:opacity-50"
          >
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AdminDashboard;
